/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
	type Klass,
	type LexicalNode,
	LexicalNodeReplacement,
	ParagraphNode,
	TextNode,
} from "lexical";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { HashtagNode } from "@lexical/hashtag";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { MarkNode } from "@lexical/mark";
import { OverflowNode } from "@lexical/overflow";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ImageNode } from "./ImageNode";
import { KeywordNode } from "./KeywordNode";
import { MentionNode } from "./MentionNode";
import { LayoutContainerNode } from "./LayoutContainerNode";
import { LayoutItemNode } from "./LayoutItemNode";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ExtendedHeadingNode, ExtendedParagraphNode, ExtendedTableCellNode, ExtendedTableNode, ExtendedTextNode } from "./overrides";

const EditorNodes: ReadonlyArray<
	Klass<LexicalNode> | LexicalNodeReplacement
> = [
	ListNode,
	ListItemNode,
	QuoteNode,
	CodeNode,
	HashtagNode,
	CodeHighlightNode,
	AutoLinkNode,
	LinkNode,
	OverflowNode,
	ImageNode,
	MentionNode,
	KeywordNode,
	HorizontalRuleNode,
	MarkNode,
	LayoutContainerNode,
	LayoutItemNode,
	TableRowNode,
	TableCellNode,
	ExtendedTextNode,
	{
		replace: TextNode,
		with: (node: TextNode) => new ExtendedTextNode(node.__text),
	},
	ExtendedTableNode,
	{
		replace: TableNode,
		with: (_: TableNode) => new ExtendedTableNode(),
	},
	ExtendedHeadingNode,
	{
		replace: HeadingNode,
		with: (_node: HeadingNode) => new ExtendedHeadingNode(_node.__tag),
	},
	ExtendedParagraphNode,
	{
		replace: ParagraphNode,
		with: (_: ParagraphNode) => new ExtendedParagraphNode(),
	},
	ExtendedTableCellNode,
	{
		replace: TableCellNode,
		with: (_: TableCellNode) => new ExtendedTableCellNode(),
	},
];

export default EditorNodes;
